// src/components/TargetPriceModal.js

import React, { useState, useEffect, useMemo } from 'react';
import './TargetPriceModal.css';

const formatCurrency = (value) => new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'EUR' }).format(value || 0);
const formatPercent = (value) => `${(value || 0).toFixed(6)} %`;

const TargetPriceModal = ({ isOpen, onClose, onSave, item }) => {
    const [targetRP, setTargetRP] = useState(0);
    const [targetCP, setTargetCP] = useState(0);
    const [isDirty, setIsDirty] = useState(false);

    const listPrice = useMemo(() => parseFloat(item?.List_Price) || 0, [item]);
    const ourPrice = useMemo(() => {
        if (!item) return 0;
        const opPrice = (parseFloat(item.List_Price) || 0) * (1 - (parseFloat(item.OP_Discount) || 0) / 100);
        const asCostUnit = parseFloat(item.AsCost_Unit) || 0;
        return opPrice + asCostUnit;
    }, [item]);

    useEffect(() => {
        if (item) {
            const currentRP = listPrice * (1 - (parseFloat(item.RP_Discount) || 0) / 100);
            const currentCP = currentRP * (1 - (parseFloat(item.CP_Discount) || 0) / 100);
            setTargetRP(Number(currentRP.toFixed(2))); // Zaokrouhlíme pro zobrazení
            setTargetCP(Number(currentCP.toFixed(2))); // Zaokrouhlíme pro zobrazení
            setIsDirty(false);
        }
    }, [item, listPrice]);

    const calculatedDiscounts = useMemo(() => {
        if (listPrice === 0) return { rpDiscount: 0, cpDiscount: 0 };
        const rpDiscount = (1 - (targetRP / listPrice)) * 100;
        const cpDiscount = targetRP === 0 ? 0 : (1 - (targetCP / targetRP)) * 100;
        return { rpDiscount, cpDiscount };
    }, [targetRP, targetCP, listPrice]);
    
    const margin = useMemo(() => {
        if (ourPrice === 0 && targetCP > 0) return Infinity;
        if (ourPrice === 0 && targetCP <= 0) return 0;
        return ((targetCP - ourPrice) / ourPrice) * 100;
    }, [targetCP, ourPrice]);

    const handleValueChange = (setter, value) => {
        setter(parseFloat(value) || 0);
        setIsDirty(true);
    };

    const handleSave = () => {
        if (targetRP < 0 || targetCP < 0) {
            alert("Prices cannot be negative.");
            return;
        }
        const finalRpDiscount = parseFloat(calculatedDiscounts.rpDiscount.toFixed(6));
        const finalCpDiscount = parseFloat(calculatedDiscounts.cpDiscount.toFixed(6));
        onSave(item.ROWID, finalRpDiscount, finalCpDiscount);
    };

    if (!isOpen || !item) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content target-price-modal">
                <h2>Target Price Calculator</h2>
                <h3 className="item-title">{item.Product_Name}</h3>

                <div className="price-context">
                    <span>List Price: <strong>{formatCurrency(listPrice)}</strong></span>
                    <span>Our Price (OP): <strong>{formatCurrency(ourPrice)}</strong></span>
                </div>

                <div className="input-section">
                    <div className="form-group">
                        <label htmlFor="targetRP">Target Reference Price (RP)</label>
                        <input
                            type="number"
                            id="targetRP"
                            step="1" // Důležitý atribut pro krok po centech
                            value={targetRP}
                            onChange={(e) => handleValueChange(setTargetRP, e.target.value)}
                        />
                        <div className="calculated-value">
                            New RP Discount: <strong>{formatPercent(calculatedDiscounts.rpDiscount)}</strong>
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="targetCP">Target Customer Price (CP)</label>
                        <input
                            type="number"
                            id="targetCP"
                            step="1" // Důležitý atribut pro krok po centech
                            value={targetCP}
                            onChange={(e) => handleValueChange(setTargetCP, e.target.value)}
                        />
                        <div className="calculated-value">
                            New CP Discount: <strong>{formatPercent(calculatedDiscounts.cpDiscount)}</strong>
                        </div>
                    </div>
                </div>

                <div className="summary-section">
                    New Margin: <strong className={margin < 0 ? 'negative-margin' : ''}>{margin.toFixed(2)} %</strong>
                </div>

                <div className="modal-actions">
                    <button onClick={onClose} className="btn-secondary">Cancel</button>
                    <button onClick={handleSave} className="btn-primary" disabled={!isDirty}>Apply & Save</button>
                </div>
            </div>
        </div>
    );
};

export default TargetPriceModal;